import axios from 'axios';
import { DISCOUNT_ERROR_CITY, DISCOUNT_ERROR_INVALID, DISCOUNT_ERROR_USED, discountErrorFromStatus } from "@/app/lib/discountErrors";
import api from './api';
import { Dispatch } from 'redux';
import { RootState } from '../store';

// Action Types
const CREATE_ORDER_REQUEST = 'CREATE_ORDER_REQUEST';
const CREATE_ORDER_SUCCESS = 'CREATE_ORDER_SUCCESS';
const CREATE_ORDER_FAILURE = 'CREATE_ORDER_FAILURE';
const GET_ORDER_REQUEST = 'GET_ORDER_REQUEST';
const GET_ORDER_SUCCESS = 'GET_ORDER_SUCCESS';
const GET_ORDER_FAILURE = 'GET_ORDER_FAILURE';
const EMPTY_CART_SUCCESS = 'EMPTY_CART_SUCCESS';
const CHECK_DISCOUNT_REQUEST = 'CHECK_DISCOUNT_REQUEST';
const CHECK_DISCOUNT_SUCCESS = 'CHECK_DISCOUNT_SUCCESS';
const CHECK_DISCOUNT_FAILURE = 'CHECK_DISCOUNT_FAILURE';
const RESET_DISCOUNT = 'RESET_DISCOUNT';
export const resetDiscount = () => ({ type: RESET_DISCOUNT });
// Code accepte a la verification mais refuse par le devis du serveur : retire, avec son message.
//  - discount_reserved : reserve a un autre compte (la verification en exempte l'equipe, la
//    commande non) -> « code invalide » ;
//  - discount_already_used : code « une fois par compte » deja utilise -> « deja utilise ».
export const discountRefused = (quoteCode?: string) => ({
  type: CHECK_DISCOUNT_FAILURE,
  error: quoteCode === 'discount_already_used' ? DISCOUNT_ERROR_USED : DISCOUNT_ERROR_INVALID,
});

// Action Interfaces
interface CreateOrderRequestAction {
  type: typeof CREATE_ORDER_REQUEST;
}

interface CreateOrderSuccessAction {
  type: typeof CREATE_ORDER_SUCCESS;
  payload: any;
}

interface CreateOrderFailureAction {
  type: typeof CREATE_ORDER_FAILURE;
  error: string;
}

interface GetOrderRequestAction {
  type: typeof GET_ORDER_REQUEST;
}

interface GetOrderSuccessAction {
  type: typeof GET_ORDER_SUCCESS;
  payload: any;
}

interface GetOrderFailureAction {
  type: typeof GET_ORDER_FAILURE;
  error: string;
}

interface EmptyCartSuccessAction {
  type: typeof EMPTY_CART_SUCCESS;
}

interface CheckDiscountRequestAction {
  type: typeof CHECK_DISCOUNT_REQUEST;
}

interface CheckDiscountSuccessAction {
  type: typeof CHECK_DISCOUNT_SUCCESS;
  payload: {
    isValid: boolean;
    discountUuid: string | null;   // <-- FIXED: you dispatch this field
    discountAmount: number | null;
    discountType: string | null;
    allowedCityIds?: number[];
  };
}

interface CheckDiscountFailureAction {
  type: typeof CHECK_DISCOUNT_FAILURE;
  error: string;
}

type OrderActions =
  | CreateOrderRequestAction
  | CreateOrderSuccessAction
  | CreateOrderFailureAction
  | GetOrderRequestAction
  | GetOrderSuccessAction
  | GetOrderFailureAction
  | EmptyCartSuccessAction
  | CheckDiscountRequestAction
  | CheckDiscountSuccessAction
  | CheckDiscountFailureAction
  | { type: string; payload?: any };

// State Type
interface OrderState {
  loading: boolean;
  order: any | null;
  error: string | null;
  discountValid: boolean | null;
  discountAmount: number | null;
  discountType: string | null;
  discountUuid: string | null;
  discountAllowedCityIds: number[] | null;
}

// Initial State
const initialState: OrderState = {
  loading: false,
  order: null,
  error: null,
  discountValid: null,
  discountAmount: null,
  discountType: null,
  discountUuid: null,
  discountAllowedCityIds: null,
};

// ---------- Actions ----------

// Accept either legacy string ("wire_transfer" or "pi_...") OR object with new fields
type PaymentArg =
  | string
  | {
      paymentProvider?: 'stripe' | 'wire' | 'gyg' | 'coins' | 'free' | 'pay_later' | 'monthly_invoice';
      paymentIntentId?: string;    // 'pi_...'
      paymentIntent?: string;      // legacy field if you still want to send it
    };

interface CreateOrderOptions {
  coinsUsed?: number;
  coinsValueEUR?: number;
  coinsValueCurrency?: number;
  originalPrice?: number;
}

export const createOrder = (
  amount: string,
  currency: string,
  payment: PaymentArg,              // <-- was paymentIntentId: string
  cartItemUuids: string[],
  discountCode?: string | null,
  options: CreateOrderOptions = {}
) => async (dispatch: Dispatch<OrderActions | any>, getState: () => RootState) => {
  dispatch({ type: CREATE_ORDER_REQUEST });

  const userString = localStorage.getItem('user');
  const languageCode = localStorage.getItem('lang');
  let accessToken = '';
  let userUuid = '';

  if (userString) {
    const user = JSON.parse(userString);
    accessToken = user.access_token || '';
    userUuid = user.user?.uuid || '';
  }

  try {
    const state = getState();

    // Ensure `discountCode` is a valid string
    const discountCodeValue = discountCode ?? state.orders.discountUuid ?? '';

    // Build request body
    const body: any = {
      price: amount,
      currency,
      cartItems: cartItemUuids,
      discountCode: discountCodeValue,
      comingFrom: 'frontend',
      userUuid,
      createdBy: userUuid,
      language: languageCode,
    };

    // Normalize `payment` (supports legacy string or new object form)
    if (typeof payment === 'string') {
      // legacy: "wire_transfer", "pi_...", or "GYG"
      body.paymentIntent = payment;

      if (payment.startsWith('pi_')) {
        // Stripe card flow
        body.paymentProvider = 'stripe';
        body.paymentIntentId = payment;
      } else if (payment === 'wire_transfer') {
        body.paymentProvider = 'wire';
      } else if (payment === 'GYG') {
        body.paymentProvider = 'gyg';
      }
    } else if (payment && typeof payment === 'object') {
      // new form
      const { paymentIntent, paymentProvider, paymentIntentId } = payment as {
        paymentIntent?: string;
        paymentProvider?: 'stripe' | 'wire' | 'gyg' | 'coins' | 'free' | 'pay_later' | 'monthly_invoice';
        paymentIntentId?: string;
      };
      if (paymentIntent) body.paymentIntent = paymentIntent;      // keep legacy column filled
      if (paymentProvider) body.paymentProvider = typeof paymentProvider === 'string' ? paymentProvider.toLowerCase() : paymentProvider; // server gate
      if (paymentIntentId) body.paymentIntentId = paymentIntentId; // 'pi_...'
    }

    if (typeof options.originalPrice === 'number') {
      body.originalPrice = options.originalPrice;
    }
    if (typeof options.coinsUsed === 'number') {
      body.coinsUsed = options.coinsUsed;
    }
    if (typeof options.coinsValueEUR === 'number') {
      body.coinsValueEUR = options.coinsValueEUR;
    }
    if (typeof options.coinsValueCurrency === 'number') {
      body.coinsValueCurrency = options.coinsValueCurrency;
    }


    const response = await api.post(
      `/api/create-order`,
      body,
      { headers: { Authorization: `Bearer ${accessToken || ''}` } }
    );

    dispatch({ type: CREATE_ORDER_SUCCESS, payload: response.data });
    dispatch({ type: RESET_DISCOUNT });
    dispatch({ type: EMPTY_CART_SUCCESS });

    const orderUuid = response.data?.order?.uuid;
    const remainingCoins = typeof response.data?.remainingCoins === 'number'
      ? response.data.remainingCoins
      : undefined;

    // `settlement` is present only on the deferred rails: it carries the debit date the
    // server computed in the tour city's timezone, so the client never re-derives it.
    return { orderUuid, remainingCoins, settlement: response.data?.settlement };
  } catch (error) {
    // "Shows as failed but actually went through": the order may ALREADY EXIST.
    // The backend answers 409 with the existing order's uuid — that's a SUCCESS,
    // not a failure. Recover it instead of throwing.
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      const existingUuid = error.response?.data?.orderUuid || error.response?.data?.order?.uuid;
      if (existingUuid) {
        dispatch({ type: CREATE_ORDER_SUCCESS, payload: error.response.data });
        dispatch({ type: RESET_DISCOUNT });
        dispatch({ type: EMPTY_CART_SUCCESS });
        return { orderUuid: existingUuid, remainingCoins: undefined };
      }
    }

    let errorMessage = 'An unknown error occurred';
    if (axios.isAxiosError(error)) {
      errorMessage = error.response?.data?.message || error.message;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    dispatch({ type: CREATE_ORDER_FAILURE, error: errorMessage });
    throw error;
  }
};

// Verify whether an order actually exists for a charge — used by checkout after an
// ambiguous create-order failure (lost/slow response), so a booking that really went
// through isn't shown to the customer as a failure. Polls a few times (the order may
// be committed a moment after the client's create-order call timed out).
export const verifyOrderByPaymentIntent = (paymentIntentId: string) => async (): Promise<string | null> => {
  if (!paymentIntentId) return null;
  const userString = localStorage.getItem('user');
  let accessToken = '';
  if (userString) {
    try { accessToken = JSON.parse(userString).access_token || ''; } catch { /* ignore */ }
  }
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const { data } = await api.get(`/api/order/by-payment-intent`, {
        params: { paymentIntentId },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const uuid = data?.order?.uuid;
      if (uuid) return uuid;
    } catch (e) {
      /* 404 = no order (yet) — keep polling */
    }
    if (attempt < 2) await new Promise((r) => setTimeout(r, 1500));
  }
  return null;
};

export const getOrder = (orderId: string) => async (dispatch: Dispatch<OrderActions | any>) => {
  dispatch({ type: GET_ORDER_REQUEST });

  const userString = localStorage.getItem('user');
  let accessToken = '';

  if (userString) {
    const user = JSON.parse(userString);
    accessToken = user.access_token || '';
  }

  try {
    const response = await api.get(`/api/order/${orderId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    dispatch({ type: GET_ORDER_SUCCESS, payload: response.data });
    return response.data;
  } catch (error) {
    let errorMessage = 'An unknown error occurred';
    if (axios.isAxiosError(error)) {
      errorMessage = error.response?.data?.message || error.message;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    dispatch({ type: GET_ORDER_FAILURE, error: errorMessage });
    throw error;
  }
};

const extractCityId = (item: any): number | null => {
  const candidates = [
    item?.tour?.cityId,
    item?.tour?.city?.id,
    item?.tour?.city?.cityId,
    item?.cityId,
    item?.city?.id,
  ];
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) continue;
    const num = typeof candidate === 'string' ? parseInt(candidate, 10) : candidate;
    if (!Number.isNaN(num)) {
      return num;
    }
  }
  return null;
};

export const checkDiscountCode = (discountCode: string, cartCityId?: number | null) => async (dispatch: Dispatch<OrderActions | any>, getState: () => RootState) => {
  dispatch({ type: CHECK_DISCOUNT_REQUEST });

  try {
    const response = await api.post('/api/discount/isValidate', { discountCode });

    if (response.data.isValid) {
      const allowedCityIds: number[] = response.data.discount?.allowedCityIds || [];
      const cartItems = getState()?.cart?.items || [];
      const cityIdsInCart = cartItems
        .map((item: any) => extractCityId(item))
        .filter((id: number | null) => id !== null) as number[];

      // Optional override
      if (cartCityId !== undefined && cartCityId !== null) {
        cityIdsInCart.push(Number(cartCityId));
      }

      if (allowedCityIds.length > 0) {
        const hasAllowed = cityIdsInCart.some((id) => allowedCityIds.includes(Number(id)));
        if (!hasAllowed) {
          return dispatch({
            type: CHECK_DISCOUNT_FAILURE,
            error: DISCOUNT_ERROR_CITY,
          });
        }
      }

      dispatch({
        type: CHECK_DISCOUNT_SUCCESS,
        payload: {
          isValid: true,
          discountUuid: response.data.discount.uuid, // <-- included in payload type
          discountAmount: response.data.discount.value,
          discountType: response.data.discount.type,
          allowedCityIds,
        },
      });
    } else {
      dispatch({ type: CHECK_DISCOUNT_FAILURE, error: DISCOUNT_ERROR_INVALID });
    }
  } catch (error) {
    // 404 (code inconnu ou reserve a un autre compte) et 400 (expire, desactive, deja
    // utilise) : code invalide ; 401 : pas connecte. Avant, tout finissait en « Error applying ».
    dispatch({
      type: CHECK_DISCOUNT_FAILURE,
      error: discountErrorFromStatus((error as any)?.response?.status, (error as any)?.response?.data?.code),
    });
  }
};

// ---------- Reducer ----------

const orderReducer = (state: OrderState = initialState, action: OrderActions | any): OrderState => {
  switch (action.type) {
    case CREATE_ORDER_REQUEST:
    case GET_ORDER_REQUEST:
    case CHECK_DISCOUNT_REQUEST:
      return { ...state, loading: true, error: null };

    case CREATE_ORDER_SUCCESS:
    case GET_ORDER_SUCCESS:
      return { ...state, loading: false, order: action.payload };

    case CREATE_ORDER_FAILURE:
    case GET_ORDER_FAILURE:
      return { ...state, loading: false, error: action.error };

    case CHECK_DISCOUNT_SUCCESS:
      return {
        ...state,
        loading: false,
        discountValid: action.payload.isValid,
        discountAmount: action.payload.discountAmount,
        discountType: action.payload.discountType,
        discountUuid: action.payload.discountUuid,
        discountAllowedCityIds: action.payload.allowedCityIds || null,
        error: null,
      };

    case RESET_DISCOUNT:
      return {
        ...state,
        // `null` = aucun code applique ; `false` = code verifie et REFUSE. La
        // remise etait remise a `false` ici, et « Appliquer » commence par la
        // reinitialiser : l'ecran affichait donc « code promo invalide » pendant
        // tout l'aller-retour serveur, y compris pour un code valide (GT-260916).
        discountValid: null,
        discountAmount: null,
        discountType: null,
        discountUuid: null,
        discountAllowedCityIds: null,
        error: null,
      };

    case CHECK_DISCOUNT_FAILURE:
      return {
        ...state,
        loading: false,
        discountValid: false,
        discountAmount: null,
        discountType: null,
        discountUuid: null,
        discountAllowedCityIds: null,
        error: action.error,
      };

    case EMPTY_CART_SUCCESS:
      return { ...state, order: { ...state.order, items: [] } };

    default:
      return state;
  }
};

export default orderReducer;
